/**
 * SKU Form Drawer Component
 * Create/Edit SKU form in a drawer
 */

import { useEffect, useMemo } from 'react'
import { useTranslation, useForm, zodResolver, z } from '@ufc/core'
import {
  BaseDrawer,
  Button,
  Input,
  Label,
  Textarea,
  Switch,
  Checkbox,
  cn,
} from '@ufc/uikit'
import { Package, Save, X } from 'lucide-react'
import type {
  SKU,
  CategorySummary,
  CreateSKURequest,
  UpdateSKURequest,
} from '../../domain/types'
import { getLocalizedName } from '../../domain/utils'

// ============================================================================
// Form Schema
// ============================================================================

const skuFormSchema = z.object({
  name: z.object({
    en: z.string().min(1, 'Name (English) is required'),
    ar: z.string().optional(),
  }),
  description: z
    .object({
      en: z.string().optional(),
      ar: z.string().optional(),
    })
    .optional(),
  isActive: z.boolean(),
  categoryDocumentIds: z.array(z.string()).optional(),
})

type SKUFormData = z.infer<typeof skuFormSchema>

// ============================================================================
// Helper Functions
// ============================================================================

function skuToFormData(sku: SKU | null): SKUFormData {
  if (!sku) {
    return {
      name: { en: '', ar: '' },
      description: { en: '', ar: '' },
      isActive: true,
      categoryDocumentIds: [],
    }
  }

  return {
    name: {
      en: sku.name.en,
      ar: sku.name.ar || '',
    },
    description: {
      en: sku.description?.en || '',
      ar: sku.description?.ar || '',
    },
    isActive: sku.isActive,
    categoryDocumentIds: sku.categories?.map((c) => c.documentId) || [],
  }
}

function formDataToCreateRequest(data: SKUFormData): CreateSKURequest {
  return {
    name: {
      en: data.name.en,
      ar: data.name.ar || undefined,
    },
    description:
      data.description?.en || data.description?.ar
        ? {
            en: data.description?.en || '',
            ar: data.description?.ar || undefined,
          }
        : undefined,
    isActive: data.isActive,
    categoryDocumentIds: data.categoryDocumentIds,
  }
}

function formDataToUpdateRequest(data: SKUFormData, documentId: string): UpdateSKURequest {
  return {
    ...formDataToCreateRequest(data),
    documentId,
  }
}

// ============================================================================
// Component Props
// ============================================================================

interface SKUFormDrawerProps {
  isOpen: boolean
  onClose: () => void
  sku: SKU | null
  categories: CategorySummary[]
  onSubmit: (data: CreateSKURequest | UpdateSKURequest) => void
  isSaving?: boolean
  locale?: 'en' | 'ar'
}

// ============================================================================
// Component
// ============================================================================

export function SKUFormDrawer({
  isOpen,
  onClose,
  sku,
  categories,
  onSubmit,
  isSaving = false,
  locale = 'en',
}: SKUFormDrawerProps) {
  const { t } = useTranslation()
  const isEditing = sku !== null
  const isMutating = isSaving

  const defaultValues = useMemo(() => skuToFormData(sku), [sku])

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<SKUFormData>({
    resolver: zodResolver(skuFormSchema),
    defaultValues,
  })

  // Reset form when drawer opens/closes or sku changes
  useEffect(() => {
    if (isOpen) {
      reset(skuToFormData(sku))
    }
  }, [isOpen, sku, reset])

  const isActive = watch('isActive')
  const selectedCategories = watch('categoryDocumentIds') || []

  const handleFormSubmit = async (data: SKUFormData) => {
    if (isEditing && sku) {
      await Promise.resolve(onSubmit(formDataToUpdateRequest(data, sku.documentId)))
      return
    }

    await Promise.resolve(onSubmit(formDataToCreateRequest(data)))
  }

  const handleCategoryToggle = (categoryId: string, checked: boolean) => {
    const current = selectedCategories
    if (checked) {
      setValue('categoryDocumentIds', [...current, categoryId])
    } else {
      setValue(
        'categoryDocumentIds',
        current.filter((id) => id !== categoryId)
      )
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose()
    }
  }

  const headerContent = (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/20">
        <Package className="h-5 w-5 text-white" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-white">
          {isEditing ? t('products.editSku') : t('products.addSku')}
        </h2>
        <p className="text-sm text-white/70">
          {isEditing ? sku?.name.en : t('products.subtitle')}
        </p>
      </div>
    </div>
  )

  return (
    <BaseDrawer
      open={isOpen}
      onOpenChange={handleOpenChange}
      headerGradient="from-primary to-primary/80"
      headerContent={headerContent}
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-6 p-6">
        {/* Name (English) */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="name-en">
            {t('products.form.nameEn')} <span className="text-destructive">*</span>
          </Label>
          <Input
            id="name-en"
            {...register('name.en')}
            placeholder={t('products.form.nameEn')}
            disabled={isMutating}
          />
          {errors.name?.en && (
            <p className="text-sm text-destructive">{errors.name.en.message}</p>
          )}
        </div>

        {/* Name (Arabic) */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="name-ar">{t('products.form.nameAr')}</Label>
          <Input
            id="name-ar"
            {...register('name.ar')}
            placeholder={t('products.form.nameAr')}
            disabled={isMutating}
            dir="rtl"
          />
        </div>

        {/* Description (English) */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="description-en">{t('products.form.descriptionEn')}</Label>
          <Textarea
            id="description-en"
            {...register('description.en')}
            placeholder={t('products.form.descriptionEn')}
            disabled={isMutating}
            rows={3}
          />
        </div>

        {/* Description (Arabic) */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="description-ar">{t('products.form.descriptionAr')}</Label>
          <Textarea
            id="description-ar"
            {...register('description.ar')}
            placeholder={t('products.form.descriptionAr')}
            disabled={isMutating}
            rows={3}
            dir="rtl"
          />
        </div>

        {/* Categories */}
        {categories.length > 0 && (
          <div className="flex flex-col gap-2">
            <Label>{t('products.form.categories')}</Label>
            <div className="border rounded-md p-3 max-h-48 overflow-y-auto">
              {categories.map((category) => (
                <div key={category.documentId} className="flex items-center gap-2 py-1">
                  <Checkbox
                    id={`category-${category.documentId}`}
                    checked={selectedCategories.includes(category.documentId)}
                    onCheckedChange={(checked) =>
                      handleCategoryToggle(category.documentId, !!checked)
                    }
                    disabled={isMutating}
                  />
                  <Label
                    htmlFor={`category-${category.documentId}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {getLocalizedName(category.name, locale)}
                    {category.code && (
                      <span className="text-muted-foreground ml-2">({category.code})</span>
                    )}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Status */}
        <div className="flex items-center justify-between py-2 border rounded-md px-3">
          <Label htmlFor="is-active" className="cursor-pointer">
            {t('products.form.isActive')}
          </Label>
          <Switch
            id="is-active"
            checked={isActive}
            onCheckedChange={(checked) => setValue('isActive', checked)}
            disabled={isMutating}
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onClose} disabled={isMutating}>
            <X className="h-4 w-4 me-2" />
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isMutating}>
            <Save className={cn('h-4 w-4 me-2', isSaving && 'animate-spin')} />
            {isSaving ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </form>
    </BaseDrawer>
  )
}
